<template>
    <div :class="[isGenerated && 'generated-cover-container', 'image-container', setCustomClass]"
         @click="$emit('click')"
         role="button"
         tabindex="0"
         :aria-label="thumbnailActionLabel"
         @keydown.enter.prevent="activateThumbnail"
         @keydown.space.prevent="activateThumbnail"
         :style="[getBackgroundColor]"
    >
        <div class="thumbnail-visual">
            <div v-if="isGenerated && this.caseStudy.thumbnailData && this.layouts" class="background-color-container">
                <div :class="['thumbnail-preview', thumbnailSize]" :style="backgroundStyle">
                    <SvgBackground
                        v-if="this.caseStudy.thumbnailData.background.style === 'gradient'"
                        class="background-image"
                        :style="svgBackgroundColor"
                        :variant="this.caseStudy.thumbnailData.background.gradient"
                    />
                    <div ref="mockupsContainer" class="mockups-container" :class="[primaryMockupDeviceClass, this.caseStudy.thumbnailData.layout.version, thumbnailSize, 'align-text-' + largeThumbnailTextPosition]" :style="{ height: dynamicHeight + 'px' }">
                        <div v-bind:key="index + '-mockup'" v-for="(mockup, index) in this.layouts[this.caseStudy.thumbnailData.layout.type][this.caseStudy.thumbnailData.layout.version].mockupTypes" :class="`mockup-container mockup-${index}`">
                            <Mockup
                                :svg-src="getMockupSrc(index)"
                                :image-src="getMockupContentSrc(mockup, index)"
                                :mockup="getMockupByIndex(index)"
                                :isThumbnail="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <img v-else-if="!!getImageUrl" :src="getImageUrl" class="project-thumbnail" :alt="imageAlt">
        </div>
        <slot />
    </div>
</template>

<script>

import mobile_placeholder from "../../../images/thumbnail-generation/default-images/mobile-placeholder.png";
import laptop_placeholder_v1
    from "../../../images/thumbnail-generation/default-images/laptop-placeholder-aspect-v1.png";
import laptop_placeholder_v2
    from "../../../images/thumbnail-generation/default-images/laptop-placeholder-aspect-v2.png";
import tablet_placeholder from "../../../images/thumbnail-generation/default-images/tablet-placeholder.png";
import desktop_placeholder from "../../../images/thumbnail-generation/default-images/desktop-placeholder.png";
import mobile_single_middle from "../../../images/thumbnail-generation/layouts/mobile/mobile_single_middle.svg";
import mobile_single_slided from "../../../images/thumbnail-generation/layouts/mobile/mobile_single_slided.svg";
import mobile_dual_middle from "../../../images/thumbnail-generation/layouts/mobile/mobile_dual_middle.svg";
import mobile_dual_slided from "../../../images/thumbnail-generation/layouts/mobile/mobile_dual_slided.svg";
import mobile_triple_middle from "../../../images/thumbnail-generation/layouts/mobile/mobile_triple_middle.svg";
import mobile_triple_slided from "../../../images/thumbnail-generation/layouts/mobile/mobile_triple_slided.svg";
import mobile_triple_middle_horizontal
    from "../../../images/thumbnail-generation/layouts/mobile/mobile_triple_middle_horizontal.svg";
import mobile_triple_slided_rotated
    from "../../../images/thumbnail-generation/layouts/mobile/mobile_triple_slided_rotated.svg";
import mobile_quadruple_slided from "../../../images/thumbnail-generation/layouts/mobile/mobile_quadruple_slided.svg";
import mobile_quadruple_slided_rotated
    from "../../../images/thumbnail-generation/layouts/mobile/mobile_quadruple_slided_rotated.svg";
import desktop_single_middle from "../../../images/thumbnail-generation/layouts/desktop/desktop_single_middle.svg";
import desktop_single_slided from "../../../images/thumbnail-generation/layouts/desktop/desktop_single_slided.svg";
import desktop_mobile_overlay from "../../../images/thumbnail-generation/layouts/desktop/desktop_mobile_overlay.svg";
import desktop_mobile_middle from "../../../images/thumbnail-generation/layouts/desktop/desktop_mobile_middle.svg";
import desktop_single_slided_big
    from "../../../images/thumbnail-generation/layouts/desktop/desktop_single_slided_big.svg";
import desktop_triple_variety from "../../../images/thumbnail-generation/layouts/desktop/desktop_triple_variety.svg";
import monitor_single_middle from "../../../images/thumbnail-generation/layouts/monitor/monitor_single_middle.svg";
import monitor_mobile_middle from "../../../images/thumbnail-generation/layouts/monitor/monitor_mobile_middle.svg";
import monitor_desktop_middle from "../../../images/thumbnail-generation/layouts/monitor/monitor_desktop_middle.svg";
import monitor_tablet_middle from "../../../images/thumbnail-generation/layouts/monitor/monitor_tablet_middle.svg";
import monitor_triple_variety from "../../../images/thumbnail-generation/layouts/monitor/monitor_triple_variety.svg";
import monitor_quadruple_variety
    from "../../../images/thumbnail-generation/layouts/monitor/monitor_quadruple_variety.svg";
import tablet_single_middle from "../../../images/thumbnail-generation/layouts/tablet/tablet_single_middle.svg";
import tablet_single_slided from "../../../images/thumbnail-generation/layouts/tablet/tablet_single_slided.svg";
import tablet_mobile_overlay from "../../../images/thumbnail-generation/layouts/tablet/tablet_mobile_overlay.svg";
import tablet_mobile_middle from "../../../images/thumbnail-generation/layouts/tablet/tablet_mobile_middle.svg";
import tablet_single_slided_big from "../../../images/thumbnail-generation/layouts/tablet/tablet_single_slided_big.svg";

import SvgBackground from "../../../scripts/sections/projectGrid/SvgBackground.vue";
import {generateSuggestedColors} from "../../common/helpers.js";
import {SuggestedColorsEnum, ThumbnailBackgroundStyle} from "../../common/enums.js";
import Mockup from "@/scripts/common/components/Mockup.vue";
import {findMockup} from "@/scripts/common/mockupStore/mockupStore.js";
import {resolveNonHexColor} from "@/scripts/common/utilities/colorUtils.js";
import {convertRichTextHTMLToPlainText} from "@/scripts/common/components/textEditor/utilities/textConverterUtilities.js";
import {mapGetters} from "vuex";

const defaultImages = {
    mobile:mobile_placeholder,
    laptop:{
        v1:laptop_placeholder_v1,
        v2:laptop_placeholder_v1,
        v3:laptop_placeholder_v2

    },
    tablet:tablet_placeholder,
    desktop:desktop_placeholder
}

export default {
    name: 'project-thumbnail',
    components: {Mockup, SvgBackground},
    props: {
        caseStudy: {
            default: null,
            type: Object,
            required: true
        },
        forceSquared: {
            default: false, // Fix for 'Read more of my case studies' in Footer to only use squared thumbnails
            type: Boolean
        },
        isFirst: {
            default: false,
            type: Boolean
        }
    },
    data() {
        return {
            dynamicHeight: 0,
            resizeObserver: null,
            placeholder: mobile_placeholder,
            layouts: {
                mobile: {
                    single_middle: {type: 'mobile', svg: mobile_single_middle, mockupTypes: ['mobile']},
                    single_slided: {type: 'mobile', svg: mobile_single_slided, mockupTypes: ['mobile']},
                    dual_middle: {type: 'mobile', svg: mobile_dual_middle, mockupTypes: ['mobile', 'mobile']},
                    dual_slided: {type: 'mobile', svg: mobile_dual_slided, mockupTypes: ['mobile', 'mobile']},
                    triple_middle: {
                        type: 'mobile',
                        svg: mobile_triple_middle,
                        mockupTypes: ['mobile', 'mobile', 'mobile']
                    },
                    triple_slided: {
                        type: 'mobile',
                        svg: mobile_triple_slided,
                        mockupTypes: ['mobile', 'mobile', 'mobile']
                    },
                    //Second round
                    triple_middle_horizontal: {
                        type: 'mobile',
                        svg: mobile_triple_middle_horizontal,
                        mockupTypes: ['mobile', 'mobile', 'mobile']
                    },
                    triple_slided_rotated: {
                        type: 'mobile',
                        svg: mobile_triple_slided_rotated,
                        mockupTypes: ['mobile', 'mobile', 'mobile']
                    },
                    quadruple_slided: {
                        type: 'mobile',
                        svg: mobile_quadruple_slided,
                        mockupTypes: ['mobile', 'mobile', 'mobile', 'mobile']
                    },
                    quadruple_slided_rotated: {
                        type: 'mobile',
                        svg: mobile_quadruple_slided_rotated,
                        mockupTypes: ['mobile', 'mobile', 'mobile', 'mobile']
                    },

                },
                desktop: {
                    single_middle: {type: 'desktop', svg: desktop_single_middle, mockupTypes: ['laptop']},
                    single_slided: {type: 'desktop', svg: desktop_single_slided, mockupTypes: ['laptop']},
                    mobile_overlay: {
                        type: 'desktop',
                        svg: desktop_mobile_overlay,
                        mockupTypes: ['laptop', 'mobile']
                    },
                    mobile_middle: {
                        type: 'desktop',
                        svg: desktop_mobile_middle,
                        mockupTypes: ['laptop', 'mobile']
                    },
                    //Second round
                    single_slided_big: {type: 'desktop', svg: desktop_single_slided_big, mockupTypes: ['laptop']},
                    triple_variety: {
                        type: 'desktop',
                        svg: desktop_triple_variety,
                        mockupTypes: ['laptop', 'tablet', 'mobile']
                    }
                },
                monitor: {
                    single_middle: {type: 'monitor', svg: monitor_single_middle, mockupTypes: ['desktop']},
                    mobile_middle: {
                        type: 'monitor',
                        svg: monitor_mobile_middle,
                        mockupTypes: ['desktop', 'mobile']
                    },
                    desktop_middle: {
                        type: 'monitor',
                        svg: monitor_desktop_middle,
                        mockupTypes: ['desktop', 'laptop']
                    },
                    tablet_middle: {
                        type: 'monitor',
                        svg: monitor_tablet_middle,
                        mockupTypes: ['desktop', 'tablet']
                    },
                    triple_variety: {
                        type: 'monitor',
                        svg: monitor_triple_variety,
                        mockupTypes: ['desktop', 'tablet', 'mobile']
                    },
                    quadruple_variety: {
                        type: 'monitor',
                        svg: monitor_quadruple_variety,
                        mockupTypes: ['desktop', 'tablet', 'mobile', 'laptop']
                    }
                },
                tablet: {
                    single_middle: {type: 'tablet', svg: tablet_single_middle, mockupTypes: ['tablet']},
                    single_slided: {type: 'tablet', svg: tablet_single_slided, mockupTypes: ['tablet']},
                    mobile_overlay: {type: 'tablet', svg: tablet_mobile_overlay, mockupTypes: ['tablet', 'mobile']},
                    mobile_middle: {type: 'tablet', svg: tablet_mobile_middle, mockupTypes: ['tablet', 'mobile']},
                    //Second round
                    single_slided_big: {type: 'tablet', svg: tablet_single_slided_big, mockupTypes: ['tablet']},
                },
            },
        }
    },
    methods: {
        activateThumbnail(event) {
            event.currentTarget?.click();
        },
        getMockupByIndex(index) {
            const mockupDetails = this.caseStudy?.thumbnailData?.mockupDetails;

            if (!Array.isArray(mockupDetails) || !mockupDetails.length) {
                return null;
            }

            return mockupDetails[index] || mockupDetails[0];
        },
        getMockupSrc(index) {
            const mockup = this.getMockupByIndex(index);

            if (!mockup) {
                return null;
            }

            return findMockup(
                mockup.deviceType,
                mockup.mockupType,
                mockup.version,
                mockup.colorScheme,
                mockup.settings || [],
                mockup.orientation
            );
        },
        getMockupContentSrc(layoutType, imageNum) {
            if (this.caseStudy.thumbnailData.images[imageNum]) {
                if (this.caseStudy.thumbnailData.images[imageNum].cropped && this.caseStudy.thumbnailData.images[imageNum].cropped.url) {
                    return this.caseStudy.thumbnailData.images[imageNum].cropped.url
                }
                if (this.caseStudy.thumbnailData.images[imageNum].url) {
                    return this.caseStudy.thumbnailData.images[imageNum].url
                }
            }

            return layoutType === 'laptop' ? this.versionedDefaultImage(layoutType) : defaultImages[layoutType.toLowerCase()]
        },
        versionedDefaultImage(type) {
            return defaultImages[type.toLowerCase()][this.getMockupByIndex(0)?.version || 'v3']
        },
        // These 'calculateDynamicHeight' & 'resizeObserver' is needed because of a Firefox bug not calculating height correctly
        // if 'aspect-ratio' is set on parent and child has 'width: fit-content'
        // We need the height set in px after every thumbnail-layout change, generation and deletion as well as side panel open & close
        calculateDynamicHeight() {
            this.$nextTick(() => {
                if(this.$refs.mockupsContainer) {
                    // noinspection JSSuspiciousNameCombination
                    this.dynamicHeight = this.$refs.mockupsContainer.offsetWidth;
                }
            });
        },
        initResizeObserver() {
            this.$nextTick(() => {
                if (this.$refs.mockupsContainer) {
                    this.resizeObserver = new ResizeObserver(this.calculateDynamicHeight);
                    this.resizeObserver.observe(this.$refs.mockupsContainer);
                }
            });

        },
        destroyResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },
    },
    computed: {
        ...mapGetters("design", ["colorPalette", "colorStyles"]),
        thumbnailSize() {
            // Fix for 'Read more of my case studies' in Footer to only use squared thumbnails
            if (this.forceSquared) return 'small';

            if(this.$store.state.portfolio.templateSettings.thumbnailLayout && this.$store.state.portfolio.templateSettings.thumbnailLayout.includes('mixed')) {
                if(this.$store.state.portfolio.templateSettings.thumbnailLayout.includes('large')) {
                    if(this.isFirst) {
                        return 'large';
                    } else {
                        return 'small';
                    }
                }
            } else {
                return this.$store.state.portfolio.templateSettings.tileDisplayMode ? this.$store.state.portfolio.templateSettings.tileDisplayMode.split('-')[0] : 'small';
            }

        },
        largeThumbnailTextPosition() {
            let templateSettings = this.$store.state.portfolio.templateSettings;
            if(templateSettings.tileDisplayMode === 'large' &&
                templateSettings.projectTitle.placement === 'on-thumbnail' &&
                templateSettings.projectTitle.visibility === 'visible') {
                return templateSettings.projectTitle.alignment.horizontal
            }
            else {
                return 'center'
            }
        },
        suggestedColorsUsed() {
            return this.caseStudy?.thumbnailData?.background?.color && !this.caseStudy.thumbnailData.background.color.startsWith('#') && Object.values(SuggestedColorsEnum).includes(this.caseStudy.thumbnailData.background.color)
        },
        suggestedColorOptions() {
            return generateSuggestedColors(this.colorStyles, this.$store.state.portfolio.designSettings.colorPalette)
        },
        svgBackgroundColor() {
            return {
                color: this.suggestedColorsUsed
                    ? this.suggestedColorOptions[this.caseStudy.thumbnailData.background.style.match(/^[^_]*/)[0]][this.caseStudy.thumbnailData.background.color][0]
                    : resolveNonHexColor(this.caseStudy.thumbnailData.background.color, this.colorPalette)
            }
        },
        backgroundStyle() {
            let style = {};

            if(this.caseStudy.thumbnailData.background.style === ThumbnailBackgroundStyle.SOLID_COLOR) {
                style['background-color'] = this.suggestedColorsUsed
                    ? this.suggestedColorOptions[this.caseStudy.thumbnailData.background.style.match(/^[^_]*/)[0]][this.caseStudy.thumbnailData.background.color][0]
                    : resolveNonHexColor(this.caseStudy.thumbnailData.background.color, this.colorPalette);
            }
            else if(this.caseStudy.thumbnailData.background.style === ThumbnailBackgroundStyle.TRANSPARENT) {
                style['background-color'] = 'transparent';
            }
            else if(this.caseStudy.thumbnailData.background.style === ThumbnailBackgroundStyle.GRADIENT) {
                style['background-color'] = this.suggestedColorsUsed
                    ? this.suggestedColorOptions[this.caseStudy.thumbnailData.background.style.match(/^[^_]*/)[0]][this.caseStudy.thumbnailData.background.color][1]
                    : resolveNonHexColor(this.caseStudy.thumbnailData.background.accentColor, this.colorPalette);
            }else if(this.caseStudy.thumbnailData.background.style === ThumbnailBackgroundStyle.UPLOAD_IMAGE) {
                const imageUrl = this.caseStudy.thumbnailData.background.image?.cropped?.url || this.caseStudy.thumbnailData.background.image?.url;
                if (imageUrl) {
                    style['background-image'] = `url(${imageUrl})`;
                    style['background-size'] = 'cover';
                    style['background-position'] = 'center';
                } else {
                    style['background-color'] = 'var(--asphalt90)';
                }
            }else if(this.caseStudy.thumbnailData.background.style === ThumbnailBackgroundStyle.CURATED_BACKGROUNDS) {
                const imageUrl = this.caseStudy.thumbnailData.background.image?.url;
                if (imageUrl) {
                    style['background-image'] = `url(${imageUrl})`;
                    style['background-size'] = 'cover';
                    style['background-position'] = 'center';
                } else {
                    style['background-color'] = 'var(--asphalt90)';
                }
            }else {
                //Fallback
                style['background-color'] = '#FFFFFF';
            }
            return style;
        },
        isGenerated() {
            return this.caseStudy.settings.usingGeneratedThumbnail;
        },
        mainHeaderSection() {
            return this.caseStudy.sections.find(e => e.type === "MainHeader");
        },
        getBackgroundColor() {
            let bgColor = this.mainHeaderSection.backgroundSettings?.backgroundColor
                ? this.mainHeaderSection.backgroundSettings?.backgroundColor.startsWith("#")
                    ? this.mainHeaderSection.backgroundSettings?.backgroundColor
                    : `#${this.mainHeaderSection.backgroundSettings?.backgroundColor}`
                : null;

            const hasTransparentGeneratedThumbnail = this.isGenerated
                && this.caseStudy.thumbnailData?.background?.style === ThumbnailBackgroundStyle.TRANSPARENT;

            return {backgroundColor: !hasTransparentGeneratedThumbnail && !this.caseStudy.image.url ? bgColor : null}
        },
        getImageUrl() {
            return this.caseStudy.image.croppedImageUrl ?
                this.caseStudy.image.croppedImageUrl :
                this.caseStudy.image.url
                    ? this.caseStudy.image.url
                    : !!this.mainHeaderSection.image.croppedImageUrl
                        ? this.mainHeaderSection.image.croppedImageUrl
                        : this.mainHeaderSection.image.url
                            ? this.$store.state.portfolio.templateSettings.theme === "theme_fifteen" ?
                                '/placeholder-images/template-15-thumbnail-' + this.caseStudy.order % 3  + '.svg'
                                :
                                this.mainHeaderSection.image.url
                            : !this.mainHeaderSection.backgroundSettings?.backgroundColor
                                ? '/example_project_images/v2/empty-project-thumbnail.jpg'
                                : null
        },
        showOverlay() {
            return !!this.mainHeaderSection.overlaySettings?.overlay && this.mainHeaderSection.overlaySettings?.overlay.a > 0 && !this.caseStudy.image.url ;
        },
        showBlur() {
            return !!this.mainHeaderSection.overlaySettings?.blur && !this.caseStudy.image.url ;
        },
        setCustomClass() {
            return [this.showOverlay ? 'overlay' : null, this.showBlur ? 'blur' : null].toString().replace(',', ' ');
        },
        thumbnailHeight() {
            const aspectRatio = this.thumbnailSize === 'large' ? 2 : this.thumbnailSize === 'medium' ? 1.15 : 1

            return `${this.width / aspectRatio}px`
        },
        imageAlt() {
            const customAlt = this.caseStudy?.image?.altTag?.trim();
            if (customAlt) {
                return customAlt;
            }
            const caseStudyName = this.caseStudyTitle;
            return caseStudyName ? `${caseStudyName} thumbnail` : 'Project thumbnail';
        },
        thumbnailActionLabel() {
            const caseStudyName = this.caseStudyTitle;
            return caseStudyName ? `Open case study ${caseStudyName}` : 'Open case study';
        },
        caseStudyTitle() {
            const directName = this.caseStudy?.name?.trim();
            if (directName) {
                return directName;
            }

            const heroTitle = this.caseStudy?.sections?.[0]?.title?.text;
            if (heroTitle) {
                const plainTitle = convertRichTextHTMLToPlainText(heroTitle)?.trim();
                if (plainTitle) {
                    return plainTitle;
                }
            }

            return '';
        },
        primaryMockupDeviceClass() {
            const deviceType = this.getMockupByIndex(0)?.deviceType;
            return deviceType ? deviceType.toLowerCase() : 'mobile';
        },
    },
    mounted() {
        this.$nextTick(() => {
           if (this.isGenerated) {
               this.initResizeObserver();
               this.calculateDynamicHeight();
           }
        });

    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    watch: {
        thumbnailSize() {
            if (this.isGenerated) {
                this.calculateDynamicHeight();
            }
        },
        isGenerated(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.initResizeObserver();
                    this.calculateDynamicHeight();
                });
            } else {
                this.destroyResizeObserver();
            }
        },
        'caseStudy.thumbnailData': {
            handler() {
                if (this.isGenerated) {
                    this.$nextTick(() => {
                        this.initResizeObserver();
                        this.calculateDynamicHeight();
                    });
                }
            },
            deep:true
        }
    },
}

</script>

<style lang="scss">
.generated-cover-container  {
    .mockups-container {
        .mockup-container {
            .mockup-svg-container {
                svg {
                    width: auto;
                }
            }
        }
    }
}
</style>

<style scoped lang="scss">

.image-container {
    cursor: pointer;

    .project-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.generated-cover-container {
    grid-column: 1 / span 12;

    &.image-container {
        padding-top: 0% !important;
        height: 100% !important;
    }

    .background-color-container {

        width: 100%;

        .thumbnail-preview {
            position: relative;
            background-repeat: no-repeat;
            overflow: hidden;

            &.large {
                aspect-ratio: 2;
            }

            //Helper text
            .project-name {
                position: absolute;
                top: 0;
                left: 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                text-align: center;
                z-index: 2;
                width: 100%;
                height: 100%;
                padding: 25px;

                &.top {
                    justify-content: flex-start;
                }

                &.bottom {
                    justify-content: flex-end;
                }

                &.left {
                    align-items: flex-start;
                    text-align: left;
                }

                &.right {
                    align-items: flex-end;
                    text-align: right;
                }
            }

            .background-image {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
            }

            .mockups-container {
                z-index: 3;

                &.small {
                    width: 100%;
                    aspect-ratio: 1;
                }

                &.medium {
                    margin: auto;

                    width: 100%;
                    aspect-ratio: 1.15;

                    //Edge case, when mockup is on the side, we have to push to right
                    &.laptop {
                        &.single_slided {
                            margin-left: 50px;
                        }

                        &.mobile_middle {
                            margin-left: 50px;
                        }
                    }
                }

                &.large {
                    margin: auto;

                    height: 100%;
                    width: 50%;
                    aspect-ratio: 2;
                }

                position: relative;


                .mockup-container {
                    left: 0;
                    right: 0;
                    top: 0;
                    bottom: 0;
                    position: absolute;
                    width: fit-content;
                    margin: auto;

                    .mockup-svg {
                        width: auto;
                        height: 100%;
                        position: relative;
                        z-index: 1;
                    }

                    img {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        max-width: 100%;
                        max-height: 100%;

                    }
                }

                //Base image widths and positions
                .mockup-container {
                    .no_device-tablet-image, .no_device-mobile-image, .no_device-laptop-image, .no_device-desktop-image {
                        width: 100%;
                        height: 100%;
                    }
                }

                //For no-device disable the border radius for all images
                &.no_device {
                    .mockup-svg {
                        visibility: hidden;
                    }

                    &.squared {
                        img {
                            border-radius: 0;
                        }
                    }

                    &.rounded {
                        img {
                            border-radius: 9px;
                        }
                    }
                }

                &.tablet .mockup-container {
                    height: 90%;
                }

                &.laptop .mockup-container {
                    height: 61%;
                }

                &.mobile .mockup-container {
                    height: 79%;
                }

                &.desktop .mockup-container {
                    height: 78%;
                }

                &.small {
                    overflow: hidden;
                }

                &.mobile {
                    &.single_slided {
                        .mockup-container {
                            height: 125%;
                            top: 40%;
                        }
                    }
                    &.dual_middle {

                        .mockup-1 {
                            left: 40%;
                        }

                        .mockup-0 {
                            right: 40%;
                        }
                    }
                    &.dual_slided {

                        .mockup-1 {
                            bottom:10%;
                            left: 40%;
                        }

                        .mockup-0 {
                            top: 10%;
                            right: 40%;
                        }
                    }
                    &.triple_middle {
                        .mockup-1 {
                            height: 51%;
                            right: 68%;
                        }

                        .mockup-2 {
                            height: 51%;
                            left: 68%;
                        }


                    }
                    &.triple_middle_horizontal {
                        .mockup-1 {
                            left: 72%;
                        }

                        .mockup-2 {
                            right: 72%;
                            left: unset;
                        }
                    }
                    &.triple_slided {
                        .mockup-0 {
                            right: 43%;
                        }

                        .mockup-1 {
                            left: 43%;
                            bottom: 80%;
                        }

                        .mockup-2 {
                            left: 43%;
                            top: 80%;
                        }

                        &.browser {
                            .mockup-1 {
                                bottom: 83%;
                            }

                            .mockup-2 {
                                top: 83%;
                            }
                        }

                    }
                    &.triple_slided_rotated {
                        .mockup-container {
                            transform: rotate(-30deg);
                        }
                        .mockup-0 {
                            right: 33%;
                            top: 20%;
                        }

                        .mockup-1 {
                            bottom: 91%;
                            left: -2%;
                        }

                        .mockup-2 {
                            left: 69%;
                            top: 47%;
                        }

                        &.browser {
                            .mockup-1 {
                                bottom: 90%;
                                left: -10%;
                            }

                            .mockup-2 {
                                left: 65%;
                                top: 48%;
                            }
                        }

                        &.no_device {
                            .mockup-1 {
                                bottom: 87%;
                                left: -8%;
                            }
                            .mockup-2 {
                                left: 64%;
                                top: 44%;
                            }
                        }
                    }
                    &.quadruple_slided {
                        .mockup-0 {
                            right: 42%;
                            bottom: 62%;
                        }

                        .mockup-1 {
                            bottom: 92%;
                            left: 42%;
                        }

                        .mockup-2 {
                            left: 42%;
                            top: 67%;
                        }
                        .mockup-3 {
                            right: 42%;
                            top: 92%;
                        }

                        &.browser {
                            .mockup-2 {
                                top: 70%;
                            }
                            .mockup-3 {
                                top: 100%;
                            }
                        }

                        &.no_device {
                            .mockup-2 {
                                top: 60%;
                            }
                            .mockup-3 {
                                top: 90%;
                            }
                        }
                    }
                    &.quadruple_slided_rotated {
                        .mockup-container {
                            transform: rotate(-30deg);
                        }

                        .mockup-0 {
                            bottom: 72%;
                            right: 43%;
                        }

                        .mockup-1 {
                            bottom: 35%;
                            left: 66%;
                        }

                        .mockup-2 {
                            left: 38%;
                            top: 66%;
                        }
                        .mockup-3 {
                            left: unset;
                            right: 66%;
                            top: 40%;
                        }

                        &.browser {
                            .mockup-0 {
                                bottom: 75%;
                                right: 45%;
                            }
                            .mokup-2 {
                                left: 340%;
                                top: 68%;
                            }
                        }

                        &.no_device {
                            .mockup-0 {
                                bottom: 70%;
                                right: 41%;
                            }
                            .mokup-2 {
                                left: 36%;
                                top: 64%;
                            }
                        }
                    }
                }

                &.laptop {
                    &.single_slided {
                        .mockup-container {
                            height: 71%;
                        }

                        .mockup-0 {
                            left: 5%;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                left: 15%;
                            }
                        }
                    }
                    &.mobile_overlay {
                        .mockup-0 {
                            height: 55%;
                            right: 5%;
                        }
                        .mockup-1 {
                            img {
                                z-index: 1;
                            }
                            height: 45%;
                            left: 51%;
                            top: 14%;
                        }
                    }
                    &.mobile_middle {
                        .mockup-0 {
                            height: 65%;
                            left: 31%;
                        }
                        .mockup-1 {
                            height: 50%;
                            right: 62%;
                        }
                    }
                    &.single_slided_big  {
                        .mockup-0 {
                            height: 105%;
                            left: -2%;
                            top: 22%;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                left: -7%;
                                top: 10%;
                            }
                        }
                    }
                    &.triple_variety {
                        .mockup-0 {
                            height: 50%;
                            right: 10%;
                        }
                        .mockup-1 {
                            height: 45%;
                            z-index: 1;
                            left: 47%;
                            top: 17%;
                        }

                        .mockup-2 {
                            height: 35%;
                            top: 35%;
                            left: 74%;
                            z-index: 2;
                        }
                    }

                }

                &.tablet {
                    &.single_slided {
                        .mockup-container {
                            height: 117%;
                            top: 30%;
                        }
                    }
                    &.mobile_overlay {
                        .mockup-0 {
                            top: -1%;
                        }
                        .mockup-1 {
                            img {
                                z-index: 1;
                            }
                            height: 58%;
                            top: 36%;
                            left: 59%;
                        }
                    }
                    &.mobile_middle {
                        .mockup-0 {
                            left: 29%;
                        }
                        .mockup-1 {
                            right: 66%;
                            height: 50%;
                        }
                    }
                    &.single_slided_big  {
                        .mockup-0 {
                            height: 160%;
                            left: 5%;
                            top: 67%;
                        }

                        &.brower {
                            .mockup-0 {
                                left: 5%
                            }
                        }
                        &.no_device {
                            .mockup-0 {
                                left: 15%;
                            }
                        }
                    }
                }

                &.desktop {
                    &.single_middle {
                        &.browser {
                            .mockup-0 {
                                height: 71%;
                            }
                        }
                    }
                    &.mobile_middle {
                        .mockup-0 {
                            height: 60%;
                            right: 15%;
                        }

                        .mockup-1 {
                            height: 36%;
                            z-index: 1;
                            left: 54%;
                            top: 30%;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                height: 61%;
                            }
                        }
                    }

                    &.tablet_middle {
                        .mockup-0 {
                            height: 60%;
                            right: 15%;
                        }

                        .mockup-1 {
                            height: 45%;
                            z-index: 1;
                            left: 55%;
                            top: 25%;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                height: 61%;
                            }
                        }
                    }

                    &.desktop_middle {
                        .mockup-0 {
                            height: 60%;
                            right: 15%;
                        }

                        .mockup-1 {
                            height: 31%;
                            z-index: 1;
                            left: 48%;
                            top: 34%;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                height: 61%;
                            }
                        }
                    }

                    &.triple_variety {
                        .mockup-0 {
                            height: 60%;
                            right: 15%;
                        }

                        .mockup-1 {
                            height: 45%;
                            z-index: 1;
                            left: 47%;
                            top: 25%;
                        }

                        .mockup-2 {
                            height: 36%;
                            top: 41%;
                            left: 74%;
                            z-index: 2;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                height: 61%;
                            }
                        }
                    }

                    &.quadruple_variety {
                        .mockup-0 {
                            height: 60%;
                        }

                        .mockup-1 {
                            height: 45%;
                            z-index: 1;
                            left: 55%;
                            top: 27%;
                        }

                        .mockup-2 {
                            height: 34%;
                            top: 42%;
                            left: 78%;
                            z-index: 2;
                        }

                        .mockup-3 {
                            height: 36%;
                            right: 40%;
                            top: 34%;
                            z-index: 2;
                        }

                        &.no_device, &.browser {
                            .mockup-0 {
                                height: 61%;
                            }
                        }
                    }
                }

                //size specific positions
                //Need to tweak for large a bit
                &.large {
                    //Align mockup based on text position in inside mockup
                    &.align-text-left {
                        margin-right: 0;

                        &.laptop {
                            &.single_slided {
                                margin-right: -10px;
                            }
                            &.single_slided_big {
                                margin-right: -25px;
                            }
                        }

                        &.tablet {
                            &.mobile_middle {
                                margin-right: 15px;
                            }
                            &.single_slided_big {
                                margin-right: -20px;
                            }
                        }

                    }

                    &.align-text-right {
                        margin-left: 0;
                        &.laptop {
                            &.mobile_middle {
                                transform: scaleX(-1);
                                .mockup-container {
                                    transform: scaleX(-1);
                                }
                            }
                            &.single_slided {
                                margin-left: -30px;

                            }
                            &.single_slided_big {
                                margin-left: -25px;
                            }
                        }
                        &.tablet {
                            &.mobile_middle {
                                transform: scaleX(-1);
                                margin-left: 15px;
                                .mockup-container {
                                    transform: scaleX(-1);
                                }
                            }

                            &.single_slided_big {
                                margin-left: -25px;
                            }
                        }
                    }

                    &.tablet {
                        &.single_slided_big {
                            .mockup-0 {
                                height: 140%;
                                left: 5%;
                                top: 65%;
                            }
                        }
                        &.mobile_middle {
                            .mockup-0 {
                                left: 50%;
                            }

                            .mockup-1 {
                                right: 50%;
                            }
                        }
                    }
                    &.laptop {
                        &.single_slided_big {
                            .mockup-0 {
                                height: 90%;
                                top: 25%;
                                left: -20%;
                            }
                        }

                        &.mobile_middle {
                            .mockup-0 {
                                left: 25%;
                            }
                            .mockup-1 {
                                right: 100%;
                            }
                        }
                    }

                    &.desktop {
                        &:not(.align-text-right):not(.align-text-left) {
                            &.quadruple_variety {
                                .mockup-0 {
                                    height: 60%;
                                }

                                .mockup-1 {
                                    left: 70%;
                                }

                                .mockup-2 {
                                    top: 45%;
                                    left: 92%;
                                }
                                .mockup-3 {
                                    left: unset;
                                    right: 66%;
                                }

                                &.no_device, &.browser {
                                    .mockup-0 {
                                        height: 45%;
                                    }
                                }
                            }
                        }
                    }
                }

                &.medium {
                    &.mobile {
                        &.triple_middle_horizontal {
                            .mockup-1 {
                                left: 78%;
                            }
                            .mockup-2 {
                                right: 78%;
                            }
                        }
                    }
                    &.laptop {
                        &.single_slided_big {
                            margin-left: 80px;
                        }
                        &.single_slided {
                            margin-left: 80px;
                        }
                    }

                    &.tablet {
                        &.single_slided_big {
                            margin-left: 80px;
                        }
                    }
                }
            }
        }
    }
}

</style>
